import React, {Fragment, useState} from 'react';
import {AlertTitle, Snackbar, Stack, Skeleton, Typography} from '@mui/material';
import {useLocation} from 'react-router-dom';

import {HeaderTabNavigation} from 'presentation/components';
import {Tab} from 'presentation/components/HeaderTabNavigation/types';
import {BillStatusEnum, MyPaymentsEnum} from 'presentation/constants';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {useNewBillsBemobiFeatureFlag} from 'presentation/pages/private/hooks/useNewBillsBemobiFeatureFlag';
import {FinancialCardV2} from 'presentation/pages/private/home/components/FinancialCardV2';
import {translations} from 'presentation/translations-new';

import {BillsErrorState} from '../../components/BillsErrorState';
import {FilterDateHeader} from './components/FilterDateHeader';
import {HistoryDetailsDrawer} from './components/HistoryDetailsDrawer';
import {HistoryEmptyState} from './components/EmptyState';
import {HistoryTable} from './components/HistoryTable';
import {useHistoryPage} from './hooks/useHistoryPage';
import {OldHistoryPage} from './historyListOld';
import {Container} from '../../styles';
import {PaymentSuccessAlert} from './styles';

const {newOpenInvoicesPage, newHistoryPage, payment} =
	translations['pt-br'].privateFlow.myFinancial;
const {paymentSuccess} = translations['pt-br'].privateFlow.myFinancial.paymentsHistory;

function HistoryPage() {
	const {isEnabled: newBillsFlagIsEnabled} = useNewBillsBemobiFeatureFlag();
	const {isMobile} = useMediaQueries();
	const {
		drawerOpen,
		selectedRow,
		historyRows,
		isLoading,
		isError,
		handleViewDetails,
		handleCloseDrawer,
		handleTryAgain,
	} = useHistoryPage();

	const location = useLocation();
	const [isSuccess, setIsSuccess] = useState(!!location.state?.paymentSuccess);

	const renderHistorySection = () => {
		if (isLoading) {
			if (isMobile) {
				return (
					<Stack gap={1}>
						{Array.from({length: 3}).map((_, i) => (
							<Skeleton key={i} height={148} sx={{transform: 'initial'}} />
						))}
					</Stack>
				);
			}
			return <Skeleton height={285} sx={{transform: 'initial'}} />;
		}

		if (isMobile) {
			return (
				<Stack gap={1}>
					{historyRows.map(row => (
						<FinancialCardV2
							key={row.id}
							hideInterestFines
							hideBorder
							referenceFirst
							dueDateLabel={payment}
							onViewDetails={() => handleViewDetails(row)}
							bill={{
								dtPrevista: row.referencia,
								cdObrigacao: Number(row.id),
								cdObrigacaoOrigem: Number(row.id),
								status: BillStatusEnum.PAID,
								flTipoPdf: false,
								flMigracao: false,
								linhaDigitavel: {
									vlObrigacao: String(row.valor),
									dtVencimento: row.vencimento,
								},
								cdEspecieTitulo: 0,
							}}
						/>
					))}
				</Stack>
			);
		}

		return <HistoryTable rows={historyRows} onViewDetails={handleViewDetails} />;
	};

	if (!newBillsFlagIsEnabled) {
		return <OldHistoryPage />;
	}

	const tabs: Tab[] = [
		{route: MyPaymentsEnum.OPEN_BILLS, name: newOpenInvoicesPage},
		{route: MyPaymentsEnum.PAYMENTS_HISTORY, name: newHistoryPage},
	];

	const isEmpty = !isLoading && historyRows.length === 0;

	if (isError) {
		return (
			<Stack>
				<HeaderTabNavigation variant={isMobile ? 'fullWidth' : 'scrollable'} tabs={tabs} />
				<BillsErrorState handleRefresh={handleTryAgain} />
			</Stack>
		);
	}

	return (
		<Fragment>
			<HeaderTabNavigation variant={isMobile ? 'fullWidth' : 'scrollable'} tabs={tabs} />
			<Container>
				<Stack gap={1.5}>
					<FilterDateHeader />
					{isEmpty ? <HistoryEmptyState /> : renderHistorySection()}
				</Stack>
			</Container>
			<HistoryDetailsDrawer open={drawerOpen} onClose={handleCloseDrawer} row={selectedRow} />
			<Snackbar
				anchorOrigin={
					isMobile
						? {vertical: 'top', horizontal: 'center'}
						: {vertical: 'top', horizontal: 'right'}
				}
				open={isSuccess}
				onClose={() => setIsSuccess(false)}
				autoHideDuration={10000}>
				<PaymentSuccessAlert severity="success">
					<AlertTitle>{paymentSuccess.title}</AlertTitle>
					<Typography mt="4px" variant="body2">
						{paymentSuccess.description}
					</Typography>
				</PaymentSuccessAlert>
			</Snackbar>
		</Fragment>
	);
}

export {HistoryPage};
